// Looks Engagement Settings Tab
// Portal-admin editable throttles for the Looks social features. Stored in
// PlatformSettings.looksSettings (DB) and read at request time — changes apply
// immediately, no redeploy, no Azure env vars needed.
import React from "react";
import SettingsSaveButtons from "./SettingsSaveButtons";

const LooksSettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
}) => {
  const toNonNegativeInt = (value, fallback) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(0, Math.floor(parsed));
  };

  const current = settings.looksSettings || {};

  const updateField = (field, value) => {
    updateSettings({
      looksSettings: { ...current, [field]: value },
    });
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const payload = {
      likePerHour: toNonNegativeInt(current.likePerHour, 30),
      commentPerHour: toNonNegativeInt(current.commentPerHour, 20),
      savePerHour: toNonNegativeInt(current.savePerHour, 30),
      repostPerHour: toNonNegativeInt(current.repostPerHour, 20),
      viewUniqueDays: toNonNegativeInt(current.viewUniqueDays, 30),
      notifyThrottleMinutes: toNonNegativeInt(current.notifyThrottleMinutes, 60),
    };
    saveSettings({ looksSettings: payload });
  };

  const numberInput = {
    width: "100%",
    padding: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
  };

  const field = (label, key, defaultValue, help) => (
    <div>
      <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>
        {label}
      </label>
      <input
        type="number"
        min="0"
        value={current[key] ?? defaultValue}
        onChange={(e) => updateField(key, toNonNegativeInt(e.target.value, defaultValue))}
        style={numberInput}
      />
      <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>{help}</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}>
          👁️ Looks Engagement Throttles
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Control how much engagement users can trigger on Looks. Settings are
          stored in the platform settings (DB) and take effect immediately —
          no redeploy or Azure changes needed. Set a per-hour limit to 0 to
          disable that action's limit.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          {field("Likes per hour (per viewer)", "likePerHour", 30,
            "Max likes per viewer per rolling hour. 0 = unlimited.")}
          {field("Comments per hour (per user)", "commentPerHour", 20,
            "Max comments per user per rolling hour. 0 = unlimited.")}
          {field("Saves per hour (per user)", "savePerHour", 30,
            "Max saved looks per user per rolling hour. 0 = unlimited.")}
          {field("Reposts per hour (per user)", "repostPerHour", 20,
            "Max reposts per user per rolling hour. 0 = unlimited.")}
          {field("Unique view window (days)", "viewUniqueDays", 30,
            "A viewer counts once per look per this many days. Repeat views inside the window do not inflate the count.")}
          {field("Notification digest window (minutes)", "notifyThrottleMinutes", 60,
            "At most one notification/digest per look per this many minutes; further events aggregate into it.")}
        </div>
      </div>

      <SettingsSaveButtons
        saving={saving}
        onCancel={resetSettings}
        hasChanges={hasUnsavedChanges}
      />
    </form>
  );
};

export default LooksSettings;
